// Display helpers + copy for the My Phone Numbers lifecycle UI.

/**
 * @param {string} raw E.164 or 10-digit US number.
 * @returns {string} "+1 (305) 555-0101" (or the input when it is not a US number).
 */
export function formatPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  const t = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return t.length === 10 ? `+1 (${t.slice(0, 3)}) ${t.slice(3, 6)}-${t.slice(6)}` : String(raw || '—');
}

/**
 * @param {{city?: string, region?: string, state?: string}} n
 * @returns {string} "City, ST" / "ST" / "City" / "—".
 */
export function locationOf(n) {
  const city = n?.city ? String(n.city).trim() : '';
  const st = (n?.region || n?.state || '').toString().trim();
  if (city && st) return `${city}, ${st}`;
  return st || city || '—';
}

/**
 * @param {string|Date|null} expiresAt
 * @param {number} [now] Epoch ms (injectable for tests).
 * @returns {{days: number, text: string, date: string}} Countdown for a Pending Deletion line.
 */
export function retentionCountdown(expiresAt, now = Date.now()) {
  if (!expiresAt) return { days: 0, text: 'Scheduled for release', date: '' };
  const d = new Date(expiresAt);
  const date = fmtDate(expiresAt);
  const days = Math.max(0, Math.ceil((d.getTime() - now) / 86400000));
  const text = days === 0 ? `Releases today (${date})` : `Expires in ${days} day${days === 1 ? '' : 's'} · release on ${date}`;
  return { days, text, date };
}

/**
 * @param {{status?: string, isDefault?: boolean, isSpecial?: boolean}} n
 * @returns {Array<{key: string, label: string, cls: string}>} Badges to render on a number card.
 */
export function numberBadges(n) {
  const out = [];
  if (n?.status === 'inactive') out.push({ key: 'inactive', label: 'Inactive (Inbound Only)', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' });
  else out.push({ key: 'active', label: 'Active', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' });
  if (n?.isDefault) out.push({ key: 'default', label: '★ Default Number', cls: 'bg-civix-purple/20 text-civix-purpleSoft border-civix-purple/40' });
  if (n?.isSpecial) out.push({ key: 'special', label: 'Special Number (First-Touch)', cls: 'bg-civix-cyan/10 text-civix-cyan border-civix-cyan/30' });
  return out;
}

export const INACTIVE_NOTE = 'Can receive calls and texts, but will not be used for outbound calls and texts for new leads. You are still charged the $1.00/mo line fee while maintaining ownership of this number.';
export const SPECIAL_NOTE = 'Used for all outbound calls to leads who have never texted you. Once a lead texts you, future touches use your regular lines.';
export const DETACH_PROMPT = 'Are you sure you want to detach this phone number from all assigned leads? The leads will revert to tenant/smart-local routing on future outbound attempts.';
/**
 * @param {string|Date|null} v
 * @returns {string} MM-DD-YYYY ("" when empty).
 */
export function fmtDate(v) {
  if (!v) return '';
  const d = new Date(v);
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
}

/**
 * Copy for the retain flow. Free while paid time remains (hold until the paid-through
 * date); otherwise the $1.00 fee buys 30 more days.
 * @param {{retentionQuote?: {free: boolean, feeUsd: number, holdUntil: string}}} number
 * @returns {{free: boolean, askTitle: string, yesLabel: string, title: string, body: string, confirmLabel: string}}
 */
export function retainCopy(number) {
  const q = number?.retentionQuote;
  if (q?.free) {
    const date = fmtDate(q.holdUntil);
    return {
      free: true,
      askTitle: `Would you like to retain this phone number until ${date}?`,
      yesLabel: `Yes, Retain Until ${date}`,
      title: 'Retain This Number',
      body: `This number is already paid through ${date}. It will move to 'Pending Deletion' and be held until then at no charge. You can restore it anytime before it is released.`,
      confirmLabel: 'Confirm (No Charge)',
    };
  }
  return {
    free: false,
    askTitle: 'Would you like to retain this phone number for 30 days?',
    yesLabel: 'Yes, Retain for 30 Days',
    title: '30-Day Number Retention',
    body: retainPrompt,
    confirmLabel: 'Confirm & Charge $1.00',
  };
}

export const retainPrompt = 'Your Telecom Wallet will be charged $1.00 for this phone number\'s final 30-day renewal holding fee. The number will be moved to the \'Pending Deletion\' section and held for 30 days. You can restore it anytime during this period.';
export const irreversiblePrompt = (number) => `Are you sure you want to permanently delete ${formatPhone(number)}? This phone number will be immediately released from your telecom account and returned to the carrier pool. This cannot be undone.`;
